import { useEffect, useState } from 'react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { Callout } from '../../components/ui/Callout';
import { useBrand } from '../../contexts/BrandContext';
import { useToast } from '../../contexts/ToastContext';
import { extractErrorMessage } from '../../api/errors';
import { useBrandSettings, useUpdateBrandSettings, type BrandRow, type BrandSettingsPatch } from '../../queries/brandSettings';
import { SecretsPanel } from './SecretsPanel';
import { VoicePanel } from './VoicePanel';
import { ImagesPanel } from './ImagesPanel';
import { EmailDesignPanel } from './EmailDesignPanel';

const inputClass = 'mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm';

type Field = { key: keyof BrandRow; label: string; type?: 'text' | 'email' | 'url' | 'color' | 'number' };

// Grouped editable columns. Identity/plumbing (domain, Cognito pools) is
// shown read-only: changing it re-points auth or DNS and belongs to Terraform.
const GROUPS: { title: string; description: string; fields: Field[] }[] = [
  {
    title: 'Identity & look',
    description: 'How the brand presents itself in the app and in email.',
    fields: [
      { key: 'name', label: 'Brand name' },
      { key: 'frontend_url', label: 'Site URL', type: 'url' },
      { key: 'primary_colour', label: 'Primary colour', type: 'color' },
      { key: 'secondary_colour', label: 'Secondary colour', type: 'color' },
      { key: 'email_header_colour', label: 'Email header colour', type: 'color' },
      { key: 'logo_primary_url', label: 'Logo URL (primary)', type: 'url' },
      { key: 'logo_secondary_url', label: 'Logo URL (secondary)', type: 'url' },
    ],
  },
  {
    title: 'Email senders',
    description: 'Transactional (receipts, verification) and marketing sender identities.',
    fields: [
      { key: 'support_email', label: 'Support email', type: 'email' },
      { key: 'admin_notification_email', label: 'Admin notification email', type: 'email' },
      { key: 'transactional_from_email', label: 'Transactional from email', type: 'email' },
      { key: 'transactional_from_name', label: 'Transactional from name' },
      { key: 'marketing_from_email', label: 'Marketing from email', type: 'email' },
      { key: 'marketing_from_name', label: 'Marketing from name' },
    ],
  },
  {
    title: 'Email warning callout',
    description: 'Palette for the cancellation/warning panel in transactional email.',
    fields: [
      { key: 'warning_bg_colour', label: 'Background', type: 'color' },
      { key: 'warning_border_colour', label: 'Border', type: 'color' },
      { key: 'warning_text_colour', label: 'Text', type: 'color' },
      { key: 'warning_panel_colour', label: 'Panel', type: 'color' },
    ],
  },
  {
    title: 'Business & compliance',
    description: 'Spam Act footer identity on every email.',
    fields: [
      { key: 'business_legal_name', label: 'Legal name' },
      { key: 'business_abn', label: 'ABN' },
      { key: 'business_address_street', label: 'Street' },
      { key: 'business_address_suburb', label: 'Suburb' },
      { key: 'business_address_state', label: 'State' },
      { key: 'business_address_postcode', label: 'Postcode' },
      { key: 'business_phone', label: 'Phone' },
    ],
  },
  {
    title: 'Brevo lists',
    description: 'List ids in this brand\'s Brevo account.',
    fields: [
      { key: 'brevo_marketing_list_id', label: 'Marketing list', type: 'number' },
      { key: 'brevo_weekly_tips_list_id', label: 'Weekly tips list', type: 'number' },
      { key: 'brevo_midweek_tips_list_id', label: 'Midweek tips list', type: 'number' },
      { key: 'brevo_free_tips_list_id', label: 'Free tips list', type: 'number' },
      { key: 'brevo_test_list_id', label: 'Test list', type: 'number' },
    ],
  },
  {
    title: 'Social posting',
    description: 'Where the daily and weekly social posts go. Leave the Facebook page blank to turn social posting off for this brand.',
    fields: [
      { key: 'facebook_page_id', label: 'Facebook page id' },
      { key: 'social_image_bucket', label: 'Image S3 bucket' },
      { key: 'social_image_base_url', label: 'Public image base URL', type: 'url' },
      { key: 'social_footer_text', label: 'Image footer text' },
    ],
  },
];

const READONLY: { key: keyof BrandRow; label: string }[] = [
  { key: 'brand_key', label: 'Brand key' },
  { key: 'primary_domain', label: 'Primary domain' },
  { key: 'customer_cognito_user_pool_id', label: 'Customer Cognito pool' },
  { key: 'admin_cognito_user_pool_id', label: 'Admin Cognito pool' },
];

function toForm(brand: BrandRow): Record<string, string> {
  const form: Record<string, string> = {};
  for (const g of GROUPS) for (const f of g.fields) form[f.key] = brand[f.key] == null ? '' : String(brand[f.key]);
  return form;
}

export function BrandSettingsPage() {
  const { brand, role } = useBrand();
  const { data, isLoading, error } = useBrandSettings();
  const update = useUpdateBrandSettings();
  const toast = useToast();
  const [form, setForm] = useState<Record<string, string>>({});
  const canWrite = role === 'super-admin' || role === 'operations';

  useEffect(() => {
    if (data) setForm(toForm(data.brand));
  }, [data]);

  if (isLoading) return <Spinner />;
  if (error || !data) return <Callout variant="error" title="Could not load brand settings">{extractErrorMessage(error, '')}</Callout>;

  const dirty: BrandSettingsPatch = {};
  for (const g of GROUPS) {
    for (const f of g.fields) {
      const original = data.brand[f.key] == null ? '' : String(data.brand[f.key]);
      if (form[f.key] !== original) {
        // Blank means "clear it" for every type; numbers go up as numbers.
        dirty[f.key] = form[f.key] === '' ? null : f.type === 'number' ? Number(form[f.key]) : form[f.key];
      }
    }
  }
  const dirtyCount = Object.keys(dirty).length;

  const save = () =>
    update.mutate(dirty, {
      onSuccess: () => toast.success(`${brand.name} settings saved.`),
      onError: (err) => toast.error(extractErrorMessage(err, 'Failed to save settings.')),
    });

  return (
    <div className="space-y-8">
      <PageHeader
        title={`${brand.name} settings`}
        description="This brand's identity, senders and provider keys. Only this brand is affected."
        actions={
          canWrite ? (
            <Button onClick={save} disabled={dirtyCount === 0 || update.isPending}>
              {update.isPending ? 'Saving…' : dirtyCount === 0 ? 'No changes' : `Save ${dirtyCount} change${dirtyCount === 1 ? '' : 's'}`}
            </Button>
          ) : undefined
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Identity (read-only)</CardTitle>
          <CardDescription>Domain and authentication pools are managed in infrastructure, not here.</CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            {READONLY.map((f) => (
              <div key={f.key}>
                <dt className="text-muted-foreground">{f.label}</dt>
                <dd className="font-mono text-xs">{String(data.brand[f.key] ?? '—')}</dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>

      <SecretsPanel secrets={data.secrets} enabled={data.secretsEnabled} canWrite={canWrite} brandName={brand.name} />

      {GROUPS.map((group) => (
        <Card key={group.title}>
          <CardHeader>
            <CardTitle>{group.title}</CardTitle>
            <CardDescription>{group.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {group.fields.map((f) => (
                <label key={f.key} className="block text-sm">
                  <span className="text-muted-foreground">{f.label}</span>
                  <div className="flex items-center gap-2">
                    {f.type === 'color' && form[f.key] && (
                      <span className="mt-1 inline-block h-6 w-6 shrink-0 rounded border border-border" style={{ backgroundColor: form[f.key] }} aria-hidden />
                    )}
                    <input
                      className={inputClass}
                      type={f.type === 'number' ? 'number' : f.type === 'color' ? 'text' : (f.type ?? 'text')}
                      value={form[f.key] ?? ''}
                      disabled={!canWrite}
                      onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                    />
                  </div>
                </label>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      <VoicePanel canWrite={canWrite} brandName={brand.name} />
      <ImagesPanel canWrite={canWrite} brandName={brand.name} />
      <EmailDesignPanel canWrite={canWrite} brandName={brand.name} />
    </div>
  );
}
